import {
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
  OnInit,
  AfterViewInit
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SimplebarAngularModule } from 'simplebar-angular';

import { filterMenuByPermissions } from '@app/layouts/sidebar/menu.utils';
import { AuthService } from '@app/auth/auth.service';
import { MenuItem } from '@app/layouts/sidebar/menu.model';
import { MENU } from '@app/menu';

@Component({
  selector: 'app-horizontal-topbar',
  templateUrl: './horizontal-topbar.component.html',
  styleUrls: ['./horizontal-topbar.component.scss'],
  standalone: true,
  imports: [TranslateModule, SimplebarAngularModule, CommonModule, RouterModule]
})
export class HorizontalTopbarComponent implements OnInit, AfterViewInit {
  // Массив элементов меню
  menuItems: MenuItem[] = [];

  // Флаг для какого-то toggle (если нужен)
  toggle: boolean = true;

  // Событие для кнопки "бургер-меню" (моб. меню)
  @Output() mobileMenuButtonClicked = new EventEmitter<void>();

  constructor(
    private router: Router,
    public translate: TranslateService,
    private authService: AuthService,
  ) {
  }

  ngOnInit(): void {
    const permissions = this.authService.permissions();
    this.menuItems = filterMenuByPermissions(MENU, permissions);
  }

  /**
   * После инициализации View ищем активный пункт и выставляем корректный класс
   */
  ngAfterViewInit(): void {
    this.initActiveMenu();
  }

  /**
   * Удаляет классы активных элементов у всех пунктов меню
   */
  removeActivation(items: Element[]): void {
    items.forEach((item: any) => {
      if (item.classList.contains('menu-link')) {
        if (!item.classList.contains('active')) {
          item.setAttribute('aria-expanded', 'false');
        }
        if (item.nextElementSibling) {
          item.nextElementSibling.classList.remove('show');
        }
      }
      if (item.classList.contains('nav-link')) {
        if (item.nextElementSibling) {
          item.nextElementSibling.classList.remove('show');
        }
        item.setAttribute('aria-expanded', 'false');
      }
      item.classList.remove('active');
    });
  }

  /**
   * Тоггл для вложенного subItem
   */
  toggleSubItem(event: any): void {
    event.preventDefault();
    const isCurrentMenuId = event.target.closest('a.nav-link');
    if (!isCurrentMenuId) return;

    const isMenu = isCurrentMenuId.nextElementSibling as HTMLElement;
    if (!isMenu) return;

    if (isMenu.classList.contains('show')) {
      isMenu.classList.remove('show');
      isCurrentMenuId.setAttribute('aria-expanded', 'false');
    } else {
      // Скрыть все открытые подменю
      const dropDowns = Array.from(document.querySelectorAll('.sub-menu'));
      dropDowns.forEach((node: any) => node.classList.remove('show'));

      // Сброс aria-expanded
      const subDropDowns = Array.from(document.querySelectorAll('.menu-dropdown .nav-link'));
      subDropDowns.forEach((submenu: any) => submenu.setAttribute('aria-expanded', 'false'));

      isCurrentMenuId.setAttribute('aria-expanded', 'true');
      isMenu.classList.add('show');
    }
  }

  /**
   * Тот же принцип, но для "extra-sub-menu", если она есть в вашем проекте
   */
  toggleExtraSubItem(event: any): void {
    event.preventDefault();
    const isCurrentMenuId = event.target.closest('a.nav-link');
    if (!isCurrentMenuId) return;

    const isMenu = isCurrentMenuId.nextElementSibling as HTMLElement;
    if (!isMenu) return;

    if (isMenu.classList.contains('show')) {
      isMenu.classList.remove('show');
      isCurrentMenuId.setAttribute('aria-expanded', 'false');
    } else {
      const dropDowns = Array.from(document.querySelectorAll('.extra-sub-menu'));
      dropDowns.forEach((node: any) => node.classList.remove('show'));

      const subDropDowns = Array.from(document.querySelectorAll('.menu-dropdown .nav-link'));
      subDropDowns.forEach((submenu: any) => submenu.setAttribute('aria-expanded', 'false'));

      isCurrentMenuId.setAttribute('aria-expanded', 'true');
      isMenu.classList.add('show');
    }
  }

  /**
   * Тоггл для родительского пункта (первого уровня)
   */
  toggleParentItem(event: any): void {
    event.preventDefault();
    const isCurrentMenuId = event.target.closest('a.nav-link');
    if (!isCurrentMenuId) return;

    // Скрываем все ранее открытые
    const dropDowns = Array.from(document.querySelectorAll('#navbar-nav .show'));
    dropDowns.forEach((node: any) => node.classList.remove('show'));

    // Сбрасываем активные элементы
    const ul = document.getElementById('navbar-nav');
    if (ul) {
      const iconItems = Array.from(ul.getElementsByTagName('a'));
      const activeIconItems = iconItems.filter((x: any) => x.classList.contains('active'));
      activeIconItems.forEach((item: any) => {
        item.setAttribute('aria-expanded', 'false');
        item.classList.remove('active');
      });
    }

    isCurrentMenuId.setAttribute('aria-expanded', 'true');
    this.activateParentDropdown(isCurrentMenuId);
  }

  /**
   * Тоггл обычного пункта меню (аналог toggleParentItem, но оставим отдельно, если нужна разная логика)
   */
  toggleItem(event: any): void {
    event.preventDefault();
    const isCurrentMenuId = event.target.closest('a.nav-link');
    if (!isCurrentMenuId) return;

    const isMenu = isCurrentMenuId.nextElementSibling as HTMLElement;
    if (!isMenu) return;

    if (isMenu.classList.contains('show')) {
      isMenu.classList.remove('show');
      isCurrentMenuId.setAttribute('aria-expanded', 'false');
    } else {
      // Скрыть все открытые подменю
      const dropDowns = Array.from(document.querySelectorAll('#navbar-nav .show'));
      dropDowns.forEach((node: any) => node.classList.remove('show'));

      // Сбрасываем активные элементы
      const ul = document.getElementById('navbar-nav');
      if (ul) {
        const iconItems = Array.from(ul.getElementsByTagName('a'));
        const activeIconItems = iconItems.filter((x: any) => x.classList.contains('active'));
        activeIconItems.forEach((item: any) => {
          item.setAttribute('aria-expanded', 'false');
          item.classList.remove('active');
        });
      }

      isCurrentMenuId.setAttribute('aria-expanded', 'true');
      isMenu.classList.add('show');
      this.activateParentDropdown(isCurrentMenuId);
    }
  }

  /**
   * Добавляет .active у текущего пункта и его родительских меню
   */
  activateParentDropdown(item: any): void {
    item.classList.add('active');

    // Проверяем, есть ли у элемента родительский .collapse.menu-dropdown
    const parentCollapseDiv = item.closest('.collapse.menu-dropdown');
    if (parentCollapseDiv) {
      parentCollapseDiv.classList.add('show');
      const parentAnchor = parentCollapseDiv.parentElement?.children[0];
      if (parentAnchor) {
        parentAnchor.classList.add('active');
        parentAnchor.setAttribute('aria-expanded', 'true');
      }
      // Проверка на уровень глубже
      const grandParentCollapse = parentCollapseDiv.parentElement?.closest('.collapse.menu-dropdown');
      if (grandParentCollapse) {
        grandParentCollapse.classList.add('show');
        const grandParentAnchor = grandParentCollapse.previousElementSibling;
        if (grandParentAnchor) {
          grandParentAnchor.classList.add('active');
          grandParentAnchor.setAttribute('aria-expanded', 'true');

          // И так далее, если нужно...
          const greatGrandParent = grandParentAnchor.closest('.collapse.menu-dropdown');
          if (greatGrandParent) {
            greatGrandParent.classList.add('show');
            const greatGrandParentAnchor = greatGrandParent.previousElementSibling;
            if (greatGrandParentAnchor) {
              greatGrandParentAnchor.classList.add('active');
              greatGrandParentAnchor.setAttribute('aria-expanded', 'true');
            }
          }
        }
      }
    }
  }

  /**
   * Обработка клика для выделения активного пункта меню.
   * Применяется, например, при клике на конечные ссылки (routerLink)
   */
  updateActive(event: any): void {
    event.preventDefault();
    const clickedLink = event.target as HTMLElement;
    if (!clickedLink) return;

    const ul = document.getElementById('navbar-nav');
    if (ul) {
      const items = Array.from(ul.querySelectorAll('a.nav-link'));
      this.removeActivation(items);
    }
    this.activateParentDropdown(clickedLink);
  }

  /**
   * Инициализирует активный пункт меню на основе window.location.pathname
   */
  initActiveMenu(): void {
    const pathName = window.location.pathname;
    const ul = document.getElementById('navbar-nav');
    if (!ul) return;

    const items = Array.from(ul.querySelectorAll('a.nav-link'));
    // Сначала снять все предыдущие "active"
    const activeItems = items.filter((x) => x.classList.contains('active'));
    this.removeActivation(activeItems);

    // Найти тот пункт, который совпадает с текущим урлом
    const matchingMenuItem = items.find(
      (x: HTMLAnchorElement) => x.pathname === pathName
    );
    if (matchingMenuItem) {
      this.activateParentDropdown(matchingMenuItem);
    }
  }

  /**
   * Проверка, есть ли у данного пункта дочерние элементы
   */
  hasItems(item: MenuItem): boolean {
    return !!item.subItems && item.subItems.length > 0;
  }

  /**
   * Переключает размер side-bar в моб. варианте
   */
  toggleMobileMenu(): void {
    const sidebarsize = document.documentElement.getAttribute('data-sidebar-size');
    if (sidebarsize === 'sm-hover-active') {
      document.documentElement.setAttribute('data-sidebar-size', 'sm-hover');
    } else {
      document.documentElement.setAttribute('data-sidebar-size', 'sm-hover-active');
    }
  }

  /**
   * Закрывает sidebar (убирает класс vertical-sidebar-enable)
   */
  SidebarHide(): void {
    document.body.classList.remove('vertical-sidebar-enable');
  }
}
